import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/**
 * Progressively reveals items from a long client-side list as the user scrolls.
 *
 * - Renders `initialBatch` items on first paint (and whenever `totalCount`
 *   shrinks below the current visible count).
 * - Watches `sentinelRef` with an IntersectionObserver — when the sentinel
 *   enters the viewport (within `rootMargin`), reveals another `loadMore`
 *   items in a `startTransition`-like low-priority update.
 * - Caps at `totalCount` and stops observing once everything is shown.
 *
 * Designed for surfaces that already have a parent scroll container (drawer
 * bodies, dialogs) where wiring up a full virtualizer would be overkill but
 * mounting hundreds of rows on tab open would still cause a jank spike.
 *
 * @example
 * ```tsx
 * const { visibleCount, sentinelRef, hasMore } = useProgressiveList(members.length);
 * return (
 *   <>
 *     {members.slice(0, visibleCount).map(...)}
 *     {hasMore && <div ref={sentinelRef} />}
 *   </>
 * );
 * ```
 */
export function useProgressiveList(
  totalCount: number,
  options?: { initialBatch?: number; loadMore?: number; rootMargin?: string },
): {
  visibleCount: number;
  hasMore: boolean;
  sentinelRef: RefObject<HTMLDivElement | null>;
  loadMore: () => void;
} {
  const initialBatch = options?.initialBatch ?? 30;
  const loadMoreSize = options?.loadMore ?? 30;
  const rootMargin = options?.rootMargin ?? "200px 0px";

  const [visibleCount, setVisibleCount] = useState(() => Math.min(initialBatch, totalCount));
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset whenever the underlying list shrinks so we don't get stuck pointing
  // past the end after a filter clears most rows. Growing lists keep their
  // current visibleCount so users don't lose their scroll progress.
  useEffect(() => {
    setVisibleCount((prev) => {
      const cap = Math.min(initialBatch, totalCount);
      if (prev > totalCount) return Math.max(cap, totalCount);
      if (totalCount === 0) return 0;
      if (prev === 0) return cap;
      return prev;
    });
  }, [totalCount, initialBatch]);

  const hasMore = visibleCount < totalCount;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + loadMoreSize, totalCount));
  }, [loadMoreSize, totalCount]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            loadMore();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loadMore, rootMargin]);

  return { visibleCount, hasMore, sentinelRef, loadMore };
}
